import { faroCoreApiClient } from "@api/faro-core-api-client";
import { createMemberOption, createTeamOption } from "@components/common/members-autocomplete/members-autocomplete-utils";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useInviteFailuresDialog } from "@components/project/invite-failures-list";
import { useMembersAutocomplete } from "@hooks/autocomplete/use-members-autocomplete";
import { useCanAssignProjectRoles } from "@hooks/feature-control/use-can-assign-project-roles";
import { useInviteXgSeats } from "@hooks/use-invite-xg-seats";
import { useCompanyMembers } from "@hooks/use-company-members";
import { useRespondToInvite } from "@hooks/use-respond-to-invite";
import { useTeamUtils } from "@hooks/use-team-utils";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { UtilsTypes } from "@stellar/utils";
import { addMemberIfMissing } from "@store/members/members-slice";
import { convertToMemberTypes } from "@store/members/members-slice-utils";
import { projectMembersSelector } from "@store/projects/projects-selector";
import { addProcessingProjects, removeProcessingProjects, setOneProjects } from "@store/projects/projects-slice";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { assignableTeamsSelector } from "@store/teams/teams-selector";
import { sortMembersByNameOrEmail } from "@utils/member-utils";
import { shouldDisableSendInvite } from "@utils/members-invite-utils";
import { doesProjectRoleNeedXgSeat, getInviteHelperText, getInviteRequestFailures, getInviteResponseFailures, isAnyoneInvited, isMissingXgSeat, PROJECT_XG_SEAT_REQUIRED_MESSAGE } from "@utils/invite-xg-seat-utils";
import { sentryCaptureError } from "@utils/sentry-utils";
import { sortTeams } from "@utils/team-utils";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo, useState } from "react";
/**
 * Returns why a member or team cannot be invited because they are in the project already, or
 * undefined when they are not
 *
 * @param projectMembers The members of the project by their identity
 * @param identity The identity of the member, or the ID of the team
 */
function getAlreadyInProjectMessage(projectMembers, identity) {
  const projectMember = projectMembers.get(identity);
  return projectMember ? `Already in project as ${UtilsTypes.RoleDisplayNames[projectMember.role]}` : undefined;
}

/**
 * Encapsulates the shared logic for inviting members and teams to a project:
 * building the autocomplete options, wiring the members autocomplete, deriving
 * the disabled state and submitting the invitation. Consumers only render the
 * surrounding UI (dialog, inline form, etc.).
 */
export function useInviteMembersToProject({
  project,
  initialRole,
  onInviteSuccess,
  location
}) {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    isTeamName
  } = useTeamUtils();
  const {
    trackEvent
  } = useTrackEvent();
  const companyMembers = useCompanyMembers();
  const teams = useAppSelector(assignableTeamsSelector);
  // To tell the members and teams behind the selected values when listing who could not be invited
  const inviteSubjects = {
    members: companyMembers,
    teams
  };
  const {
    respondToUser
  } = useRespondToInvite();
  const showFailures = useInviteFailuresDialog();
  const projectMembers = useAppSelector(projectMembersSelector);
  const [isLoading, setIsLoading] = useState(false);
  const [pickedRole, setPickedRole] = useState(initialRole);
  const canAssignProjectRoles = useCanAssignProjectRoles();

  /** Users without the feature may only invite viewers, so no role is picked. */
  const selectedRole = canAssignProjectRoles ? pickedRole : "RESOURCE_VIEWER";
  const xgSeats = useInviteXgSeats({
    isSeatRequiredForRole: doesProjectRoleNeedXgSeat(selectedRole),
    seatRequiredMessage: PROJECT_XG_SEAT_REQUIRED_MESSAGE,
    location
  });
  const {
    memberWithoutSeatDisabledMessage,
    teamDisabledMessage
  } = xgSeats;
  const projectMemberMap = useMemo(() => new Map(projectMembers.map(member => [member.identity, member])), [projectMembers]);
  const companyMemberIdentities = useMemo(() => new Set(companyMembers.map(companyMember => companyMember.identity)), [companyMembers]);

  /** Reforming company members data for stellar autocomplete component */
  const memberOptions = useMemo(() => sortMembersByNameOrEmail(companyMembers).map(member_0 => {
    const disabledMessage = getAlreadyInProjectMessage(projectMemberMap, member_0.identity) ?? (isMissingXgSeat(member_0) ? memberWithoutSeatDisabledMessage : undefined);
    return createMemberOption({
      member: member_0,
      isDisabled: !!disabledMessage,
      disabledMessage
    });
  }), [companyMembers, projectMemberMap, memberWithoutSeatDisabledMessage]);

  /** Reforming team data for stellar autocomplete component */
  const teamOptions = useMemo(() => sortTeams(teams, "name_ascending").map(team => {
    const disabledMessage_0 = getAlreadyInProjectMessage(projectMemberMap, team.id) ?? teamDisabledMessage;
    return createTeamOption({
      team,
      isDisabled: !!disabledMessage_0,
      disabledMessage: disabledMessage_0
    });
  }), [projectMemberMap, teams, teamDisabledMessage]);
  const autocompleteOptions = useMemo(() => [...teamOptions, ...memberOptions], [teamOptions, memberOptions]);
  const {
    selectedMembers,
    uniqueSelectedValues,
    helperText,
    inputHelperText,
    isMemberInputValid,
    handleMemberSelect,
    onInputChange,
    isNewEmailValid,
    reset
  } = useMembersAutocomplete({
    entityName: "project",
    existingMembers: projectMembers,
    options: autocompleteOptions,
    companyMemberIds: companyMembers.map(member_1 => member_1.identity)
  });
  const xgSeatsSelection = xgSeats.checkSelection(selectedMembers);

  /** Disable the `Send Invite` action if no role or no valid member is selected */
  const shouldSendInviteDisable = !selectedRole || isLoading || !xgSeatsSelection.isValid || shouldDisableSendInvite(uniqueSelectedValues, isMemberInputValid);

  /** Adds a newly invited member to the members store when needed. */
  function addNewMemberToStore(members) {
    if (!members) {
      return;
    }
    const newMemberResponse = members.find(allMembers => !projectMembers.some(oldMembers => oldMembers.identity === allMembers.identity));
    if (!newMemberResponse || FaroCoreApiUtils.Member.isTeam(newMemberResponse)) {
      return;
    }
    const isAlreadyExistsInWorkspace = companyMemberIdentities.has(newMemberResponse.identity);
    if (!isAlreadyExistsInWorkspace) {
      /**
       * The new member should be added to the member store as well.
       * We should use the setOne because we are updating the company role for all members
       * that are present in this list for a Project Role and we should have a Company Role.
       * If we update the role to project in the members store, we will not be able to remove
       * these members from the member page. The user needs to reload the page to have the company
       * role again.
       * It is only added when missing, since the member may have joined the workspace along the
       * invite already, e.g. to be given a seat, which a member converted from the project member
       * would lose.
       */
      dispatch(addMemberIfMissing(convertToMemberTypes(newMemberResponse)));
    }
  }

  /** Submit members to be invited */
  async function handleConfirm() {
    if (isLoading || !selectedRole || !uniqueSelectedValues.length || !xgSeatsSelection.isValid) {
      return;
    }
    setIsLoading(true);

    // The people need their seat before they can be given the role, so those that could not get
    // one are left out, while the rest are invited
    const {
      failures: seatFailures,
      failedValues,
      numberOfSeatsGiven
    } = await xgSeatsSelection.giveSeats();
    // The seats that were given stay assigned whatever fails afterwards
    const areSeatsGiven = numberOfSeatsGiven > 0;
    const identities = uniqueSelectedValues.filter(value => !failedValues.has(value));
    if (!identities.length) {
      showFailures({
        failures: seatFailures,
        isAnyInvited: false,
        areSeatsGiven
      });
      setIsLoading(false);
      return;
    }
    trackEvent({
      name: ProjectEvents.inviteMember,
      props: {
        location,
        numberOfMembers: identities.length,
        role: selectedRole
      }
    });
    const payload = {
      assignments: [{
        role: selectedRole,
        identities
      }]
    };
    try {
      // Add the projectId to processing projects to start the loading spinner in the project
      dispatch(addProcessingProjects([project.id]));
      const response = await faroCoreApiClient.projects.addMembersToProject({
        companyId,
        projectId: project.id,
        payload
      });

      // Add invited member to the store. response.data.members exist when the response is either success or warning
      if (response.data.members) {
        dispatch(setOneProjects({
          ...project,
          members: response.data.members
        }));
        addNewMemberToStore(response.data.members);
      }
      const responseFailures = getInviteResponseFailures(response, inviteSubjects);
      const failures = [...seatFailures, ...responseFailures];
      if (failures.length) {
        showFailures({
          failures,
          isAnyInvited: isAnyoneInvited(identities, responseFailures, inviteSubjects),
          areSeatsGiven
        });
      } else {
        respondToUser(response, identities.length);
      }
      await onInviteSuccess?.();
      resetSelection();
      setPickedRole(initialRole);
    } catch (error) {
      sentryCaptureError({
        error,
        title: "Cannot invite the members"
      });
      showFailures({
        failures: [...seatFailures, ...getInviteRequestFailures(identities, inviteSubjects, error)],
        isAnyInvited: false,
        areSeatsGiven
      });
    } finally {
      // Remove projectId from processing project to stop the loading spinner in the project
      dispatch(removeProcessingProjects([project.id]));
      setIsLoading(false);
    }
  }
  return {
    autocompleteOptions,
    selectedMembers,
    helperText: getInviteHelperText({
      inputHelperText,
      selectionHelperText: helperText,
      xgSeatsHelperText: xgSeatsSelection.helperText
    }),
    handleMemberSelect: options => {
      if (!isLoading) {
        handleMemberSelect(options);
      }
    },
    onInputChange,
    isValidInput: value_0 => isNewEmailValid(value_0) || isTeamName(value_0),
    getChipColor: xgSeatsSelection.getChipColor,
    giveXgSeatsCheckbox: xgSeatsSelection.giveSeatsCheckbox && {
      ...xgSeatsSelection.giveSeatsCheckbox,
      isDisabled: isLoading
    },
    selectedRole,
    setSelectedRole: setPickedRole,
    canAssignProjectRoles,
    isLoading,
    shouldSendInviteDisable,
    handleConfirm,
    // While the invite is in progress the selection is kept, e.g. when the dialog is reopened, so it
    // still shows what is being invited
    reset: () => {
      if (!isLoading) {
        resetSelection();
      }
    }
  };

  /** Resets the selection along with the choice to give seats */
  function resetSelection() {
    reset();
    xgSeats.reset();
  }
}